import { Badge, SimpleGrid, Stack, Table, Tooltip } from '@mantine/core';
import type {
  BreakdownLine,
  FigureKind,
  ScenarioResult,
} from 'src/secret-menu/billing/hooks/useRevenue';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import SectionCard from '../overview/SectionCard';
import { describeChange } from '../overview/lenses';
import { compare } from './history';
import { formatRevenue, formatSignedPence, ManualBadge, Stat, toneOf } from './ui';

/** How each kind of figure looks; its name comes from the back end with the line. */
const KIND_STYLES: Record<Exclude<FigureKind, 'MANUAL'>, { color: string; hint: string }> = {
  ACTUAL: { color: 'green', hint: 'Billed today' },
  WHAT_IF: { color: 'violet', hint: 'Changed by this scenario' },
  FORECAST: { color: 'blue', hint: 'Free-trial firms converting' },
};

function KindBadge({ line }: { line: BreakdownLine }) {
  if (line.kind === 'MANUAL') return <ManualBadge />;
  const style = KIND_STYLES[line.kind];
  return (
    <Tooltip label={style.hint} withArrow>
      <Badge size="xs" variant="light" color={style.color}>
        {line.kindLabel}
      </Badge>
    </Tooltip>
  );
}

type Props = {
  result: ScenarioResult;
  currentState: ScenarioResult;
  isCurrentState: boolean;
};

export default function ModellerResults({ result, currentState, isCurrentState }: Props) {
  const rows = result.breakdown.filter(row => row.included);
  const mrr = compare(result.mrrPence, currentState.mrrPence);
  const arr = compare(result.arrPence, currentState.arrPence);
  const tone = isCurrentState ? 'neutral' : toneOf(mrr.change);
  const badge = rows.some(row => row.kind === 'MANUAL') ? <ManualBadge /> : undefined;

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <Stat
          label="Modelled MRR"
          value={formatRevenue(result.mrrPence)}
          detail={
            isCurrentState
              ? 'Current state'
              : `${formatSignedPence(mrr.change ?? 0)} (${describeChange(result.mrrPence, currentState.mrrPence).label}) vs current state`
          }
          tone={tone}
          hint="modelledRevenue"
          badge={badge}
        />
        <Stat
          label="Modelled ARR"
          value={formatRevenue(result.arrPence)}
          detail={
            isCurrentState
              ? 'Current state'
              : `${formatSignedPence(arr.change ?? 0)} a year vs current state`
          }
          tone={tone}
          hint="modelledRevenue"
          badge={badge}
        />
      </SimpleGrid>

      <SectionCard title="Where it comes from" subtitle="Only the lines switched on">
        <Table verticalSpacing={6} style={{ fontVariantNumeric: 'tabular-nums' }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th />
              <Table.Th ta="right">A month</Table.Th>
              <Table.Th ta="right">A year</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map(row => (
              <Table.Tr key={row.key}>
                <Table.Td>
                  {row.label} <KindBadge line={row} />
                </Table.Td>
                <Table.Td ta="right">{formatRevenue(row.mrrPence)}</Table.Td>
                <Table.Td ta="right">{formatRevenue(row.arrPence)}</Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr
              style={{
                fontWeight: FONT_WEIGHTS.semibold,
                background: 'var(--mantine-color-default-hover)',
              }}
            >
              <Table.Td>Total</Table.Td>
              <Table.Td ta="right">{formatRevenue(result.mrrPence)}</Table.Td>
              <Table.Td ta="right">{formatRevenue(result.arrPence)}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </SectionCard>
    </Stack>
  );
}
